import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { sanitizeAlphanumeric, sanitizeText, sanitizeTextarea } from 'src/app/common/input-utils';
import {
  ActualizarEncabezadoFacturaPayload,
  EstadoFactura,
  FacturaEncabezado
} from 'src/app/domain/model/servicios-entregados/factura-encabezado.model';

export interface ValidarNombreFacturaEvent {
  cdfactura: string;
  previousCdfactura: string;
  nmconsecutivo: number;
}

@Component({
  selector: 'app-modificar-encabezado-factura',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, TextareaModule],
  template: `
    @if (factura) {
      <div class="p-4 md:p-5">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Nombre Encabezado *</label>
            <input
              pInputText
              [(ngModel)]="cdfactura"
              (input)="onNombreInput($event)"
              (change)="onNombreChange()"
              maxlength="12"
              class="w-full"
              [readonly]="!esEditable"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <input pInputText [ngModel]="factura.estadoDescripcion || factura.cdestado" readonly class="w-full" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Fecha (aaaa/mm/dd)</label>
            <input pInputText [ngModel]="fechaFactura" readonly class="w-full" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Oficina</label>
            <input pInputText [ngModel]="factura.oficinaDescripcion" readonly class="w-full" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Convenio</label>
            <input pInputText [ngModel]="factura.convenioDescripcion" readonly class="w-full" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <input pInputText [ngModel]="tipoFacturacion" readonly class="w-full" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Condicion Pago</label>
            <input pInputText [ngModel]="condicionPago" readonly class="w-full" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Mes</label>
            <input pInputText [ngModel]="mesPeriodo" readonly class="w-full" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Año</label>
            <input pInputText [ngModel]="anioPeriodo" readonly class="w-full" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Fecha inicial (aaaa/mm/dd)</label>
            <input pInputText [ngModel]="fechaInicial" readonly class="w-full" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Fecha Final (aaaa/mm/dd)</label>
            <input pInputText [ngModel]="fechaFinal" readonly class="w-full" />
          </div>

          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea
              pTextarea
              [(ngModel)]="dsobservaciones"
              (input)="onObservacionesInput($event)"
              rows="4"
              maxlength="200"
              class="w-full"
              [readonly]="!esEditable"
            ></textarea>
          </div>
        </div>

        <div class="flex flex-wrap justify-end gap-3 w-full">
          <p-button label="Cancelar" [outlined]="true" (onClick)="cancelar.emit()"></p-button>
          <p-button
            label="Grabar"
            icon="pi pi-check"
            (onClick)="onGuardar()"
            [disabled]="loading"
          ></p-button>
        </div>
      </div>
    }
  `
})
export class ModificarEncabezadoFacturaComponent implements OnChanges {
  private static readonly ESTADOS_EDITABLES: ReadonlySet<string> = new Set([
    EstadoFactura.INICIAL,
    EstadoFactura.CALCULADA
  ]);

  private static readonly MESES: ReadonlyArray<string> = [
    'ENERO',
    'FEBRERO',
    'MARZO',
    'ABRIL',
    'MAYO',
    'JUNIO',
    'JULIO',
    'AGOSTO',
    'SEPTIEMBRE',
    'OCTUBRE',
    'NOVIEMBRE',
    'DICIEMBRE'
  ];

  @Input() factura: FacturaEncabezado | null = null;
  @Input() loading = false;

  @Output() guardar = new EventEmitter<ActualizarEncabezadoFacturaPayload>();
  @Output() cancelar = new EventEmitter<void>();
  @Output() validarNombre = new EventEmitter<ValidarNombreFacturaEvent>();

  cdfactura = '';
  previousCdfactura = '';
  dsobservaciones = '';
  esEditable = false;
  tipoFacturacion = '';
  condicionPago = '';
  fechaFactura = '';
  fechaInicial = '';
  fechaFinal = '';
  mesPeriodo = '';
  anioPeriodo = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['factura']) {
      this.cdfactura = this.factura?.cdfactura || '';
      this.previousCdfactura = this.cdfactura;
      this.dsobservaciones = this.factura?.dsobservaciones || '';
      this.esEditable = this.validarEstadoEditable(this.factura?.cdestado);
      this.tipoFacturacion = this.factura?.tipoFacturacionDescripcion || '';
      const dias = this.factura?.condicionPagoDias ?? 0;
      const porcentaje = this.factura?.condicionPagoPorcentaje ?? 0;
      this.condicionPago = `${dias} - ${porcentaje}`;
      this.fechaFactura = this.formatearFechaConSlash(this.factura?.fefactura);
      this.fechaInicial = this.formatearFechaConSlash(this.factura?.feinicial);
      this.fechaFinal = this.formatearFechaConSlash(this.factura?.fefinal);
      this.mesPeriodo = this.obtenerNombreMes(this.factura?.dsperiodo);
      this.anioPeriodo = this.factura?.dsperiodo?.slice(0, 4) || '';
    }
  }

  onNombreInput(event: Event): void {
    if (!this.esEditable) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const sane = sanitizeAlphanumeric(sanitizeText(input.value)).toUpperCase();
    this.cdfactura = sane.slice(0, 12);
    input.value = this.cdfactura;
  }

  onObservacionesInput(event: Event): void {
    if (!this.esEditable) {
      return;
    }

    const input = event.target as HTMLTextAreaElement;
    const sane = sanitizeAlphanumeric(sanitizeTextarea(input.value));
    this.dsobservaciones = sane;
    input.value = sane;
  }

  onNombreChange(): void {
    if (!this.esEditable || !this.factura?.nmconsecutivo) {
      return;
    }

    const cdfactura = (this.cdfactura || '').trim();
    if (!cdfactura || cdfactura === this.previousCdfactura) {
      return;
    }

    this.validarNombre.emit({
      cdfactura,
      previousCdfactura: this.previousCdfactura,
      nmconsecutivo: this.factura.nmconsecutivo
    });
  }

  onGuardar(): void {
    this.guardar.emit({
      cdfactura: (this.cdfactura || '').trim(),
      dsobservaciones: (this.dsobservaciones || '').trim()
    });
  }

  private validarEstadoEditable(cdestado?: string): boolean {
    if (!cdestado) {
      return false;
    }

    return ModificarEncabezadoFacturaComponent.ESTADOS_EDITABLES.has(cdestado.trim().toUpperCase());
  }

  private obtenerNombreMes(dsperiodo?: string): string {
    if (!dsperiodo || dsperiodo.length < 6) {
      return '';
    }

    const mes = Number.parseInt(dsperiodo.slice(4, 6), 10);
    if (Number.isNaN(mes) || mes < 1 || mes > 12) {
      return '';
    }

    return ModificarEncabezadoFacturaComponent.MESES[mes - 1];
  }

  private formatearFechaConSlash(fecha?: string): string {
    if (!fecha) {
      return '';
    }

    const fechaBase = fecha.split('T')[0];
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(fechaBase)) {
      return fechaBase;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(fechaBase)) {
      return fechaBase.replace(/-/g, '/');
    }

    return fechaBase;
  }
}
